/**
 * Date-window state for a learner's enrolment.
 *
 *   - `not_yet`   — current time is before `start_date` (course hasn't opened)
 *   - `expired`   — current time is past `end_date` (course window closed)
 *   - `available` — within the window, or no dates set
 *
 * POLICY: end dates never block access — learners on leave must be able to
 * finish, and anyone may review after the window (mirrors the backend, which
 * accepts submissions past the end date). `expired` is therefore a *display*
 * state (badges, Finalized bucketing); only `not_yet` may lock a screen. Use
 * `isEnrolmentLocked` for gating.
 */

export type EnrolmentAvailability =
  | { kind: 'available' }
  | { kind: 'not_yet'; startsAt: Date }
  | { kind: 'expired'; endedAt: Date };

interface AssignmentLike {
  start_date?: string | null;
  end_date?: string | null;
}

interface AvailabilityOptions {
  /** Override "now" for tests. */
  now?: number;
  /** If the enrolment is already completed, skip the `expired` gate. */
  completed?: boolean;
}

// Date-only strings ("2026-01-01") are interpreted in the learner's local
// timezone: start_date at 00:00:00 local, end_date at the very last
// millisecond of that day local. This matches the natural reading: a course
// "ending on March 1st" is accessible all day on March 1st.
function parseAvailabilityDate(
  value: string | null | undefined,
  edge: 'start' | 'end',
): number | null {
  if (!value) return null;
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  if (dateOnly) {
    const [y, m, d] = value.split('-').map((part) => Number(part));
    if (!y || !m || !d) return null;
    if (edge === 'end') {
      return new Date(y, m - 1, d, 23, 59, 59, 999).getTime();
    }
    return new Date(y, m - 1, d, 0, 0, 0, 0).getTime();
  }
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
}

export function getEnrolmentAvailability(
  assignment: AssignmentLike | null | undefined,
  options: AvailabilityOptions = {},
): EnrolmentAvailability {
  if (!assignment) return { kind: 'available' };
  const now = options.now ?? Date.now();
  const start = parseAvailabilityDate(assignment.start_date, 'start');
  const end = parseAvailabilityDate(assignment.end_date, 'end');

  if (start != null && now < start) {
    return { kind: 'not_yet', startsAt: new Date(start) };
  }
  if (end != null && now > end && !options.completed) {
    return { kind: 'expired', endedAt: new Date(end) };
  }
  return { kind: 'available' };
}

/** Whether the enrolment blocks access. Only a future start date locks —
 * a passed end date never does (see the policy note above). */
export function isEnrolmentLocked(
  availability: EnrolmentAvailability,
): availability is { kind: 'not_yet'; startsAt: Date } {
  return availability.kind === 'not_yet';
}
